import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import type { Transform } from '@mindcloudxai_3d/core';

interface TransformEditorProps {
  onSetTransform: (id: string, transform: Transform) => void;
}

function NumberInput({
  label,
  value,
  step = 0.1,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  // 本地文本状态让用户可以自由编辑（清空、逐个删除），
  // 仅在 blur 时解析并提交数值，避免受控 input 清空时强制回写 0
  const [text, setText] = useState(() => formatValue(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(formatValue(value));
  }, [value, focused]);

  return (
    <div className="flex items-center gap-1">
      <span className="w-3 shrink-0 text-[10px] text-white/40">{label}</span>
      <input
        type="number"
        value={text}
        step={step}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          const v = parseFloat(text);
          onChange(Number.isFinite(v) ? v : 0);
        }}
        className="w-full min-w-0 rounded-md border border-white/10 bg-white/5 px-1.5 py-1 text-[11px] text-white/75 focus:border-white/20 focus:outline-none"
      />
    </div>
  );
}

function formatValue(v: number): string {
  return Number.isFinite(v) ? String(Number(v.toFixed(3))) : '0';
}

export function TransformEditor({ onSetTransform }: TransformEditorProps) {
  const { t } = useTranslation();
  const selectedAssetId = useAssetLibraryStore((s) => s.selectedAssetId);
  const assets = useAssetLibraryStore((s) => s.assets);

  const asset = assets.find((a) => a.id === selectedAssetId);

  const updateField = useCallback(
    (field: 'position' | 'rotation' | 'scale', axis: 'x' | 'y' | 'z', value: number) => {
      if (!asset) return;
      const current = asset.transform;
      const next: Transform = {
        position: current.position ? { ...current.position } : { x: 0, y: 0, z: 0 },
        rotation: current.rotation ? { ...current.rotation } : { x: 0, y: 0, z: 0 },
        scale: current.scale
          ? typeof current.scale === 'number'
            ? { x: current.scale, y: current.scale, z: current.scale }
            : { ...current.scale }
          : { x: 1, y: 1, z: 1 },
      };
      if (field === 'position' && next.position) {
        next.position[axis] = value;
      } else if (field === 'rotation' && next.rotation) {
        next.rotation[axis] = value;
      } else if (field === 'scale' && next.scale && typeof next.scale !== 'number') {
        next.scale[axis] = value;
      }
      onSetTransform(asset.id, next);
    },
    [asset, onSetTransform],
  );

  if (!asset) {
    return (
      <div className="py-3 text-center text-xs text-white/30">{t('assetLibrary.selectModel')}</div>
    );
  }

  const pos = asset.transform.position ?? { x: 0, y: 0, z: 0 };
  const rot = asset.transform.rotation ?? { x: 0, y: 0, z: 0 };
  const scale =
    typeof asset.transform.scale === 'number'
      ? { x: asset.transform.scale, y: asset.transform.scale, z: asset.transform.scale }
      : (asset.transform.scale ?? { x: 1, y: 1, z: 1 });

  return (
    <div className="flex flex-col gap-2">
      {/* 位置 */}
      <div>
        <div className="mb-0.5 text-[10px] font-medium tracking-wide text-white/40 uppercase">
          {t('assetLibrary.position')}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <NumberInput label="X" value={pos.x} onChange={(v) => updateField('position', 'x', v)} />
          <NumberInput label="Y" value={pos.y} onChange={(v) => updateField('position', 'y', v)} />
          <NumberInput label="Z" value={pos.z} onChange={(v) => updateField('position', 'z', v)} />
        </div>
      </div>

      {/* 旋转 */}
      <div>
        <div className="mb-0.5 text-[10px] font-medium tracking-wide text-white/40 uppercase">
          {t('assetLibrary.rotation')}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <NumberInput label="X" value={rot.x} onChange={(v) => updateField('rotation', 'x', v)} />
          <NumberInput label="Y" value={rot.y} onChange={(v) => updateField('rotation', 'y', v)} />
          <NumberInput label="Z" value={rot.z} onChange={(v) => updateField('rotation', 'z', v)} />
        </div>
      </div>

      {/* 缩放 */}
      <div>
        <div className="mb-0.5 text-[10px] font-medium tracking-wide text-white/40 uppercase">
          {t('assetLibrary.scaleLabel')}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <NumberInput
            label="X"
            value={scale.x}
            step={0.05}
            onChange={(v) => updateField('scale', 'x', v)}
          />
          <NumberInput
            label="Y"
            value={scale.y}
            step={0.05}
            onChange={(v) => updateField('scale', 'y', v)}
          />
          <NumberInput
            label="Z"
            value={scale.z}
            step={0.05}
            onChange={(v) => updateField('scale', 'z', v)}
          />
        </div>
      </div>
    </div>
  );
}
